import { CustomConnectorDto } from '@/models/connectors';
import { useDeleteConnectorInstanceMutation, useGetConnectorInstancesQuery } from '@/services/endpoints/workspace-connectors';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type UseSourceConnectorDisconnectArgs = {
  workspaceId: string;
  connector: CustomConnectorDto;
};

type DisconnectResult = {
  success: boolean;
  errorMessage?: string;
};

const FORCE_CONNECTOR_DISCONNECTION_KEY = 'i18n_FORCE_CONNECTOR_DISCONNECTION';

const getDisconnectErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const data = (error as { data?: unknown }).data;
    if (typeof data === 'object' && data !== null) {
      if ('message' in data && typeof (data as { message: unknown }).message === 'string') {
        return (data as { message: string }).message;
      }
      if ('error' in data && typeof (data as { error: unknown }).error === 'string') {
        return (data as { error: string }).error;
      }
    }
  }
  return fallback;
};

export const useSourceConnectorDisconnect = ({ workspaceId, connector }: UseSourceConnectorDisconnectArgs) => {
  const { t } = useTranslation('data_sources');
  const { data: instances = [] } = useGetConnectorInstancesQuery({ workspaceId });
  const [deleteInstance, { isLoading: isDisconnecting }] = useDeleteConnectorInstanceMutation();

  const connectorInstanceId = instances.find((instance) => instance.connectorId === connector.id)?.id;
  const fallbackError = t('source_connectors.disconnect_failed');

  const disconnect = useCallback(
    async (force?: boolean): Promise<DisconnectResult> => {
      if (!connectorInstanceId) {
        return { success: false, errorMessage: fallbackError };
      }

      try {
        await deleteInstance({ workspaceId, id: connectorInstanceId, force }).unwrap();
        return { success: true };
      } catch (error) {
        const rawMessage = getDisconnectErrorMessage(error, '');
        const errorMessage =
          rawMessage === FORCE_CONNECTOR_DISCONNECTION_KEY
            ? t('source_connectors.disconnect_requires_force')
            : fallbackError;
        return { success: false, errorMessage };
      }
    },
    [connectorInstanceId, deleteInstance, fallbackError, t, workspaceId]
  );

  return {
    disconnect,
    isDisconnecting,
    canDisconnect: Boolean(connectorInstanceId),
  };
};
